/**
 * СВЕТ И ОГОНЬ БОЯ: то, что ложится поверх механики ударов (configs/meleeAnimation) и
 * залпов (configs/shotAnimation). Времена полета, момент попадания и показ итога здесь не
 * меняются: они подогнаны под звук. Планы считает utils/effects/buildCombatCast, рисует
 * scenes/AbilityCastAnimation.
 *
 * Время — в миллисекундах, размеры — доли размера ячейки.
 */

export const COMBAT_CAST_COLORS = {
  fire: 0xff6a12,
  fireLight: 0xffc24a,
  fireCore: 0xfff2c4,
  smoke: 0x4a423c,
  scorch: 0x1c130d,
  dust: 0xb9a98e,
  steel: 0xdfe6ee,
  target: 0xff3b1f,
};

/**
 * ВЗМАХ: у кончика оружия горит свет цветом игрока, с него слетают искры, из-под ног
 * отряда при рывке бьет пыль
 */
export const MELEE_SLASH_CAST = {
  tipGlow: {sizeFactor: 1.15, alpha: 0.9},
  tipCore: {sizeFactor: 0.5, alpha: 1},
  embers: {count: 10, lengthFactor: 0.42, thicknessFactor: 0.09, lifeMs: 300, flyFactor: 0.6, swingFrom: 0.12, swingTo: 0.82},
  dust: {count: 2, sizeFactor: 0.62, lifeMs: 340, alpha: 0.55, flyFactor: 0.3},
  // След оружия: у самого хвоста остается такая доля яркости
  trailTailAlpha: 0.12,
};

/**
 * ПОПАДАНИЕ УДАРА: чем больше потеря численности, тем сильнее. Столбцы — ступени
 * resolveMeleeImpactTier: удар без потерь (звон о защиту), слабый, сильный, сокрушительный
 */
export const MELEE_IMPACT_CAST = {
  lossFrom: [0, 1, 3, 6],
  bloom: {durationMs: 170, alpha: 0.85},
  flash: {durationMs: 200},
  star: {durationMs: 280},
  cut: {durationMs: 240, thicknessFactor: 0.2, spreadRad: 0.5},
  ring: {durationMs: 480, gapMs: 80, alpha: 0.95},
  wave: {durationMs: 520, alpha: 0.95},
  sparks: {lengthFactor: 0.5, thicknessFactor: 0.1, lifeMs: 420, forwardShare: 0.55, coneRad: 1.25},

  bloomSizeFactor: [0.9, 1.3, 1.7, 2.2],
  flashSizeFactor: [1.1, 1.6, 2.1, 2.9],
  starSizeFactor: [1.3, 2.1, 2.8, 3.8],
  cutCount: [0, 1, 1, 2],
  cutLengthFactor: [0, 1.5, 1.9, 2.5],
  ringCount: [0, 1, 1, 2],
  ringSizeFactor: [0, 2.2, 2.8, 3.8],
  waveCount: [0, 0, 0, 1],
  waveEndFactor: [0, 0, 0, 2.3],
  sparkCount: [7, 10, 16, 26],
  sparkDistanceFactor: [0.8, 1.15, 1.5, 2.1],
  shakeMs: [0, 0, 90, 170],
  shakeIntensity: [0, 0, 0.0018, 0.0038],
};

export const MELEE_IMPACT_TIER_COUNT = MELEE_IMPACT_CAST.lossFrom.length;

/** Ступень силы попадания по потере численности от этого удара */
export const resolveMeleeImpactTier = (sizeLoss) => {
  const loss = Number.isFinite(sizeLoss) ? sizeLoss : 0;
  return MELEE_IMPACT_CAST.lossFrom.reduce((tier, from, index) => (loss >= from ? index : tier), 0);
};

/** Параметры попадания для ступени: общие плюс значения ее столбца */
export const resolveMeleeImpactCast = (tier) => {
  const cast = {...MELEE_IMPACT_CAST, tier};
  Object.keys(MELEE_IMPACT_CAST).forEach((key) => {
    if (Array.isArray(MELEE_IMPACT_CAST[key])) {
      cast[key] = MELEE_IMPACT_CAST[key][tier];
    }
  });
  return cast;
};

/**
 * СТРЕЛЫ: залп уходит со вспышкой цветом игрока, за каждой стрелой тянется светящийся
 * след, попавшая стрела высекает искры и еще мгновение дрожит, воткнувшись. Последняя
 * стрела поднимает по ячейке шестиугольную волну
 */
export const ARROW_CAST = {
  launch: {
    glow: {sizeFactor: 1.7, alpha: 0.85, durationMs: 260},
    ring: {sizeFactor: 2, alpha: 0.9, durationMs: 340},
    sparks: {count: 7, lengthFactor: 0.45, thicknessFactor: 0.09, flyFactor: 1, coneRad: 0.55, lifeMs: 260},
  },
  // След стрелы: длина в долях размера ячейки, толщина и яркий кончик
  trail: {lengthFactor: 2.5, thicknessFactor: 0.34, coreThicknessShare: 0.42, alpha: 0.95, tipSizeFactor: 0.5, tipAlpha: 0.9},
  hit: {
    star: {sizeFactor: 0.85, durationMs: 200},
    sparks: {count: 4, lengthFactor: 0.32, thicknessFactor: 0.08, flyFactor: 0.62, coneRad: 1, lifeMs: 260},
  },
  miss: {puff: {sizeFactor: 0.6, alpha: 0.55, durationMs: 340}},
  // Воткнувшаяся стрела дрожит и гаснет
  stuck: {holdMs: 300, fadeMs: 300, wobbleRad: 0.14, wobbleMs: 45, wobbleRepeats: 3},
  volley: {
    bloom: {sizeFactor: 1.9, alpha: 0.75, durationMs: 300},
    star: {sizeFactor: 2.4, durationMs: 320},
    wave: {endFactor: 2.1, durationMs: 520, alpha: 0.95},
    sparks: {perArrow: 2, max: 22, lengthFactor: 0.42, thicknessFactor: 0.09, flyFactor: 1.25, lifeMs: 420},
  },
};

/**
 * ВАЛУНЫ: на цели заранее горит прицел, катапульта выплевывает огонь и дым, камень летит
 * горящим, за ним тянутся пламя, искры и дым. Падение — огненный шар, выжженное пятно,
 * ударная волна и угольки, у последнего камня еще и волна по ячейке и удар сильнее.
 * Промах: только пыль и немного углей, камера не дрожит
 */
export const BOULDER_CAST = {
  launch: {
    flash: {sizeFactor: 1.9, alpha: 0.9, durationMs: 240},
    ring: {sizeFactor: 2.4, alpha: 0.85, durationMs: 380},
    smoke: {count: 3, sizeFactor: 0.95, alpha: 0.5, lifeMs: 620, riseFactor: 0.6, spreadFactor: 0.4},
  },
  // Прицел на цели: кольцо сжимается к моменту падения и пульсирует
  target: {fromFactor: 1.55, toFactor: 1, pulses: 3, glowAlpha: 0.7, coreAlpha: 0.95, groundSizeFactor: 2.2, groundAlpha: 0.5, leaveMs: 160},
  // Огонь вокруг камня и хвост пламени за ним
  fire: {
    glowSizeFactor: 2.5,
    glowAlpha: 1,
    coreSizeFactor: 1.5,
    coreAlpha: 1,
    tailLengthFactor: 3.1,
    tailThicknessFactor: 0.95,
    tailCoreShare: 0.5,
    tailAlpha: 0.95,
    boulderTint: 0xffcfa6,
  },
  // Что остается за камнем: дым и угольки. Шаг растет с числом камней: след не должен
  // превращать большой залп в сотни спрайтов
  trail: {
    gapMs: 42,
    gapPerBoulderMs: 8,
    // Пламя остается цепочкой гаснущих огней: из нее и складывается хвост кометы
    flame: {sizeFactor: 0.95, shrink: 0.3, alpha: 0.95, lifeMs: 300},
    smoke: {everyNth: 2, sizeFactor: 0.8, growth: 2, alpha: 0.5, lifeMs: 560, riseFactor: 0.3},
    ember: {everyNth: 2, sizeFactor: 0.2, alpha: 1, lifeMs: 320, fallFactor: 0.25, spreadFactor: 0.14},
  },
  impact: {
    scorch: {sizeFactor: 1.3, alpha: 0.72, durationMs: 1300, holdShare: 0.5},
    fireball: {sizeFactor: 1.5, alpha: 0.95, durationMs: 300},
    flash: {sizeFactor: 2, durationMs: 180},
    ring: {sizeFactor: 2.9, alpha: 0.95, durationMs: 460},
    embers: {count: 9, lengthFactor: 0.4, thicknessFactor: 0.11, flyFactor: 1.35, lifeMs: 520, squash: 0.8},
    smoke: {count: 2, sizeFactor: 1.05, alpha: 0.5, lifeMs: 820, riseFactor: 0.95, spreadFactor: 0.3},
    // Камней много: у каждого эффект мельче и угольков меньше
    crowdFrom: 4,
    crowdScale: 0.72,
  },
  miss: {
    ring: {sizeFactor: 1.9, alpha: 0.5, durationMs: 380},
    embers: {count: 3},
  },
  volley: {
    bloom: {sizeFactor: 3, alpha: 0.85, durationMs: 340},
    ring: {sizeFactor: 4.6, alpha: 0.95, durationMs: 620},
    wave: {endFactor: 2.9, durationMs: 620, alpha: 1},
    sparks: {count: 22, lengthFactor: 0.5, thicknessFactor: 0.12, flyFactor: 2.3, lifeMs: 620, squash: 0.8},
    shake: {durationMs: 230, intensity: 0.0052},
  },
};
